import React, { useCallback } from 'react';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import KanbanColumn from './KanbanColumn';
import { taskService } from '../../Services/taskService';
import type { TaskItemDto, TaskStatus } from '../../types/tasks';

const COLUMNS: TaskStatus[] = ['Todo', 'InProgress', 'InReview', 'Done'];

interface KanbanBoardProps {
  tasks: TaskItemDto[];
  onTasksChange: (tasks: TaskItemDto[]) => void;
  onTaskClick: (task: TaskItemDto) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ tasks, onTasksChange, onTaskClick }) => {
  const getTasksByStatus = useCallback(
    (status: TaskStatus): TaskItemDto[] => {
      return tasks
        .filter((t) => t.status === status)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    [tasks]
  );

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { draggableId, destination, source } = result;

      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      const newStatus = destination.droppableId as TaskStatus;

      // Optimistic update
      const updatedTasks = tasks.map((t) => {
        if (t.id === draggableId) {
          return { ...t, status: newStatus, sortOrder: destination.index };
        }
        return t;
      });
      onTasksChange(updatedTasks);

      // Persist to backend
      try {
        await taskService.moveTask(draggableId, {
          status: newStatus,
          sortOrder: destination.index,
        });
      } catch (error) {
        console.error('Failed to move task:', error);
        // Revert on failure
        onTasksChange(tasks);
      }
    },
    [tasks, onTasksChange]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 stagger-children">
        {COLUMNS.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            tasks={getTasksByStatus(status)}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;
